"use client";

import type { ChannelFeedEngagement, ChannelFeedReaction } from "@mlx/contracts";
import { MessageSquareIcon } from "lucide-react";
import Link from "next/link";

import {
  ChannelThreadReactionMenuButton,
  ChannelThreadReactionPicker,
} from "./channel-thread-reaction-picker";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { REACTION_EMOJI } from "./feed-engagement";

/*
 * A feed row's engagement as markup only: the viewer's reactions arrive as
 * props (useFeedReactions in use-feed-reactions.ts in the app, fixtures on
 * the concept board), so this module never reaches the app's data client.
 */
export type FeedReactions = {
  engagement: ChannelFeedEngagement | undefined;
  pending: boolean;
  toggle: (reaction: ChannelFeedReaction, active: boolean) => Promise<void>;
};

const REVEAL_ON_HOVER = "sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100";
const ACTION_CLASS = "size-6 rounded-full text-muted-foreground hover:text-foreground";

/*
 * A feed row's own actions, beside its time: add a reaction, reply. They
 * appear on hover or focus where there is a pointer and stay shown on a small
 * touch screen, so a quiet stream reads one line a row.
 */
export function FeedRowActions({ reactions, onOpen, href }: {
  reactions: FeedReactions | null;
  onOpen?: () => void;
  href?: string;
}) {
  return (
    <span className={cn("flex items-center", REVEAL_ON_HOVER)}>
      {reactions ? (
        <ChannelThreadReactionMenuButton
          engagement={reactions.engagement}
          disabled={reactions.pending}
          align="end"
          onToggle={reactions.toggle}
        />
      ) : null}
      {href && !onOpen ? (
        <Link href={href} aria-label="Reply" title="Reply"
          className={cn(buttonVariants({ variant: "ghost", size: "icon-xs" }), ACTION_CLASS)}>
          <MessageSquareIcon aria-hidden />
        </Link>
      ) : onOpen ? (
        <Button type="button" variant="ghost" size="icon-xs" aria-label="Reply" title="Reply"
          className={ACTION_CLASS} onClick={onOpen}>
          <MessageSquareIcon aria-hidden />
        </Button>
      ) : null}
    </span>
  );
}

/*
 * What a feed row has gathered, under its sentence: reactions and the reply
 * count. A row with neither shows nothing here.
 */
export function FeedEngagementSummary({ engagement, reactions, onOpen, href }: {
  engagement: ChannelFeedEngagement | null;
  /** The viewer's reactions to toggle; without them a reaction opens the thread. */
  reactions: FeedReactions | null;
  onOpen?: () => void;
  href?: string;
}) {
  const active = engagement?.reactions.filter((reaction) => reaction.count > 0) ?? [];
  const commentCount = engagement?.commentCount ?? 0;
  const unreadCount = engagement?.unreadCount ?? 0;
  if (active.length === 0 && commentCount === 0) return null;
  const replies = (
    <>
      <MessageSquareIcon aria-hidden />
      {`${commentCount} ${commentCount === 1 ? "reply" : "replies"}`}
      {unreadCount > 0 ? <span className="font-medium text-primary">{` · ${unreadCount} new`}</span> : null}
    </>
  );
  const tone = unreadCount > 0 ? "text-foreground" : "text-muted-foreground";
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {active.length === 0 ? null : reactions ? (
        <ChannelThreadReactionPicker engagement={engagement ?? undefined} disabled={reactions.pending}
          showAddButton={false} className="mt-0" onToggle={reactions.toggle} />
      ) : (
        active.map((reaction) => (
          <button
            key={reaction.reaction}
            type="button"
            aria-label={`Open thread with ${reaction.count} ${reaction.reaction} reactions`}
            className="inline-flex h-6 items-center gap-1 rounded-full border border-border/70 bg-background px-1.5 text-2xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            onClick={onOpen}
          >
            <span aria-hidden>{REACTION_EMOJI[reaction.reaction]}</span>
            {reaction.count}
          </button>
        ))
      )}
      {commentCount === 0 ? null : href && !onOpen ? (
        <Link href={href} className={cn(buttonVariants({ variant: "ghost", size: "xs" }), tone)}>{replies}</Link>
      ) : (
        <Button type="button" variant="ghost" size="xs" className={tone} onClick={onOpen}>{replies}</Button>
      )}
    </div>
  );
}
